import React, { useState } from 'react'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { useAuth } from '../../hooks/useAuth'

// Mock data for library
const mockLibrary = {
  categories: [
    { id: 1, name: 'Technology', count: 45, color: 'bg-blue-500' },
    { id: 2, name: 'Business', count: 32, color: 'bg-green-500' },
    { id: 3, name: 'Marketing', count: 28, color: 'bg-purple-500' },
    { id: 4, name: 'Leadership', count: 22, color: 'bg-orange-500' },
    { id: 5, name: 'Innovation', count: 18, color: 'bg-pink-500' },
    { id: 6, name: 'Digital', count: 15, color: 'bg-indigo-500' }
  ],
  content: [
    {
      id: 1,
      title: 'The Future of AI in Business',
      description: 'How artificial intelligence is transforming modern business practices and what leaders need to know.',
      category: 'Technology',
      tags: ['AI', 'Business', 'Innovation'],
      platforms: ['LinkedIn', 'Twitter'],
      status: 'published',
      createdAt: '2024-08-01',
      wordCount: 850,
      engagement: 234
    },
    {
      id: 2,
      title: 'Digital Transformation Strategies',
      description: 'Essential strategies for successful digital transformation in enterprise organizations.',
      category: 'Business',
      tags: ['Digital', 'Transformation', 'Strategy'],
      platforms: ['LinkedIn', 'Facebook'],
      status: 'draft',
      createdAt: '2024-08-02',
      wordCount: 1200,
      engagement: 0
    },
    {
      id: 3,
      title: 'Content Marketing Best Practices',
      description: 'Proven strategies for creating engaging content that drives results.',
      category: 'Marketing',
      tags: ['Marketing', 'Content', 'Strategy'],
      platforms: ['LinkedIn', 'Twitter', 'Facebook'],
      status: 'published',
      createdAt: '2024-08-03',
      wordCount: 650,
      engagement: 189
    },
    {
      id: 4,
      title: 'Leadership in the Digital Age',
      description: 'How to lead effectively in an increasingly digital and remote work environment.',
      category: 'Leadership',
      tags: ['Leadership', 'Digital', 'Remote'],
      platforms: ['LinkedIn'],
      status: 'scheduled',
      createdAt: '2024-08-04',
      wordCount: 950,
      engagement: 0
    },
    {
      id: 5,
      title: 'Innovation Management',
      description: 'Building a culture of innovation and managing creative processes effectively.',
      category: 'Innovation',
      tags: ['Innovation', 'Management', 'Culture'],
      platforms: ['LinkedIn', 'Twitter'],
      status: 'published',
      createdAt: '2024-08-05',
      wordCount: 780,
      engagement: 156
    }
  ]
}

const LibraryPage: React.FC = () => {
  const { user } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [view, setView] = useState<'grid' | 'list'>('grid')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'draft': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      case 'scheduled': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
    }
  }

  const getCategoryColor = (category: string) => {
    const cat = mockLibrary.categories.find(c => c.name === category)
    return cat?.color || 'bg-gray-500'
  }

  const filteredContent = mockLibrary.content.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus
    
    return matchesSearch && matchesCategory && matchesStatus
  })

  return (
    <div className="space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold gradient-text">
            Content Library
          </h1>
          <p className="text-muted-foreground mt-2">
            Organize and manage your content assets across all platforms
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" size="sm" className="btn-outline">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Content
          </Button>
          <Button variant="outline" size="sm" className="btn-outline">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Import
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search content, tags, or descriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-modern w-full"
          />
        </div>
        
        <div className="flex gap-3">
          <Select
            value={selectedCategory}
            onValueChange={setSelectedCategory}
            className="w-40"
          >
            <option value="all">All Categories</option>
            {mockLibrary.categories.map(category => (
              <option key={category.id} value={category.name}>
                {category.name} ({category.count})
              </option>
            ))}
          </Select>
          
          <Select
            value={selectedStatus}
            onValueChange={setSelectedStatus}
            className="w-32"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
          </Select>
          
          <div className="flex border border-border rounded-lg">
            <Button
              variant={view === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('grid')}
              className={`rounded-r-none ${view === 'grid' ? 'btn-primary' : 'btn-ghost'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </Button>
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
              className={`rounded-l-none ${view === 'list' ? 'btn-primary' : 'btn-ghost'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </Button>
          </div>
        </div>
      </div>

      {/* Categories Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {mockLibrary.categories.map((category, index) => (
          <Card key={category.id} className="card-modern cursor-pointer hover:shadow-md transition-shadow slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
            <div className="p-4 text-center">
              <div className={`w-12 h-12 rounded-full ${category.color} mx-auto mb-3 flex items-center justify-center text-white font-bold`}>
                {category.name.charAt(0)}
              </div>
              <h3 className="font-semibold text-sm mb-1">{category.name}</h3>
              <p className="text-xs text-muted-foreground">{category.count} items</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Content Grid/List */}
      {view === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContent.map((item, index) => (
            <Card key={item.id} className="card-modern hover:shadow-md transition-shadow slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-8 h-8 rounded-full ${getCategoryColor(item.category)} flex items-center justify-center text-white text-xs font-bold`}>
                    {item.category.charAt(0)}
                  </div>
                  <Badge className={`text-xs ${getStatusColor(item.status)}`}>
                    {item.status}
                  </Badge>
                </div>
                
                <h3 className="font-semibold text-lg mb-2 line-clamp-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{item.description}</p>
                
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{item.wordCount} words</span>
                    <span>{item.engagement} engagement</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {item.platforms.map(platform => (
                      <Badge key={platform} variant="secondary" className="text-xs">
                        {platform}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <Button variant="outline" size="sm" className="flex-1 btn-outline">Edit</Button>
                  <Button variant="outline" size="sm" className="flex-1 btn-outline">Schedule</Button>
                  <Button variant="outline" size="sm" className="btn-outline">...</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredContent.map((item, index) => (
            <Card key={item.id} className="card-modern slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-8 h-8 rounded-full ${getCategoryColor(item.category)} flex items-center justify-center text-white text-xs font-bold`}>
                        {item.category.charAt(0)}
                      </div>
                      <h3 className="font-semibold text-lg">{item.title}</h3>
                      <Badge className={`text-xs ${getStatusColor(item.status)}`}>
                        {item.status}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{item.wordCount} words</span>
                      <span>{item.engagement} engagement</span>
                      <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Button variant="outline" size="sm" className="btn-outline">Edit</Button>
                    <Button variant="outline" size="sm" className="btn-outline">Schedule</Button>
                    <Button variant="outline" size="sm" className="btn-outline">...</Button>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {item.platforms.map(platform => (
                      <Badge key={platform} variant="secondary" className="text-xs">
                        {platform}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredContent.length === 0 && (
        <Card className="card-modern slide-up">
          <div className="p-12 text-center">
            <svg className="w-16 h-16 text-muted-foreground mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold mb-2">No content found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filter criteria
            </p>
            <Button className="btn-primary">Create New Content</Button>
          </div>
        </Card>
      )}
    </div>
  )
}

export default LibraryPage